import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from './utils';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardVariant = 'default' | 'elevated' | 'outline' | 'muted';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  variant?: CardVariant;
}

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

const variantClasses: Record<CardVariant, string> = {
  default: 'border border-slate-200/80 bg-white shadow-sm',
  elevated: 'border border-slate-100 bg-white shadow-lg shadow-slate-950/[0.06]',
  outline: 'border border-slate-200 bg-transparent',
  muted: 'border border-slate-200/70 bg-slate-50/70',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, padding = 'none', variant = 'default', ...props },
  ref,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        'overflow-hidden rounded-2xl',
        variantClasses[variant],
        paddingClasses[padding],
        className,
      )}
    />
  );
});

Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div
        {...props}
        ref={ref}
        className={cn('flex items-start justify-between gap-4 px-5 pt-5', className)}
      />
    );
  },
);

CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...props }, ref) {
    return (
      <h2
        {...props}
        ref={ref}
        className={cn('text-base font-semibold tracking-tight text-slate-950', className)}
      />
    );
  },
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return (
    <p {...props} ref={ref} className={cn('mt-1 text-sm leading-6 text-slate-500', className)} />
  );
});

CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div {...props} ref={ref} className={cn('px-5 py-5', className)} />;
  },
);

CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return (
      <div
        {...props}
        ref={ref}
        className={cn(
          'flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-5 py-3.5',
          className,
        )}
      />
    );
  },
);

CardFooter.displayName = 'CardFooter';
